import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateCompanyMutation, useUpdateCompanyMutation } from '../../../../hooks/mutations/useCompanyMutation';
import { Company } from '../../../../services/apiSLTP/materials/companies';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getCoordsFromAdresseDataGouv } from '../../../../utils/numbers';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';
import { MuiTelInput } from 'mui-tel-input';

interface ModalProps {
    company: Company | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateCompanyModal: React.FC<ModalProps> = ({ company, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [designation, setDesignation] = useState(company?.designation || '');
    const [codeProC, setCodeProC] = useState(company?.codeProC || '');
    const [email, setEmail] = useState(company?.email || '');
    const [phone, setPhone] = useState(company?.phone || '');
    const [address, setAddress] = useState(company?.address || '');
    const [latitude, setLatitude] = useState(company?.latitude?.toString() || '');
    const [longitude, setLongitude] = useState(company?.longitude?.toString() || '');
    const [status, setStatus] = useState(company?.crudStatus || crudStatusEnum.OK);
    const [isGnrSupplier, setIsGnrSupplier] = useState(company?.isGnrSupplier || false);
    const [isSubcontractor, setIsSubcontractor] = useState(company?.isSubcontractor || false);
    const [isToolSupplier, setIsToolSupplier] = useState(company?.isToolSupplier || false);
    const [isTransportCompany, setIsTransportCompany] = useState(company?.isTransportCompany || false);
    const [isSiteSupplier, setIsSiteSupplier] = useState(company?.isSiteSupplier || false);

    const { mutate: updateCompany } = useUpdateCompanyMutation({
        onSuccess: () => refetch(),
    });

    const { mutate: createCompany } = useCreateCompanyMutation({
        onSuccess: () => refetch(),
    });

    const handleOk = () => {
        if (isEditMode) {
            if (!company) return;
            updateCompany({
                companyId: company._id,
                data: {
                    designation,
                    codeProC,
                    email,
                    crudStatus: status,
                    isGnrSupplier,
                    isSubcontractor,
                    isToolSupplier,
                    isTransportCompany,
                    isSiteSupplier,
                    phone: phone || undefined,
                    latitude: latitude ? parseFloat(latitude) : null,
                    longitude: longitude ? parseFloat(longitude) : null,
                    address,
                },
            });
        } else {
            createCompany({
                designation,
                codeProC,
                email,
                crudStatus: status,
                isGnrSupplier,
                isSubcontractor,
                isToolSupplier,
                isTransportCompany,
                isSiteSupplier,
                phone: phone || undefined,
                latitude: latitude ? parseFloat(latitude) : null,
                longitude: longitude ? parseFloat(longitude) : null,
                address,
            });
        }
    };

    useEffect(() => {
        const computePoint = async () => {
            if (!address.length || (latitude && longitude)) return;
            const point = await getCoordsFromAdresseDataGouv(address);
            if (!latitude) setLatitude(point?.lat.toString() || '');
            if (!longitude) setLongitude(point?.lon.toString() || '');
        };

        computePoint();
    }, [address, latitude, longitude]);

    return (
        <ModalBody
            title={t('company')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!designation || !codeProC}
            onSubmit={handleOk}
        >
            <CustomTextInput value={designation} label={t('designation')} onChange={setDesignation} required />
            <CustomTextInput value={codeProC} label={t('codeProC')} onChange={setCodeProC} required />
            <ToggleLine
                checked={status === crudStatusEnum.OK}
                onChange={(active) => setStatus(active ? crudStatusEnum.OK : crudStatusEnum.INACTIVE)}
                title={capitalizeFirstLetter(t('active'))}
            />
            <CustomTextInput value={address} label={t('address')} onChange={setAddress} />
            <CustomTextInput value={latitude} label={t('latitude')} onChange={(text) => setLatitude(text)} type="number" />
            <CustomTextInput value={longitude} label={t('longitude')} onChange={(text) => setLongitude(text)} type="number" />
            <CustomTextInput value={email} label={t('email')} onChange={setEmail} />
            <div className="pad-l-5px pad-r-5px">
                <MuiTelInput
                    value={phone}
                    onChange={setPhone}
                    label={capitalizeFirstLetter(t('mobile'))}
                    defaultCountry="FR"
                    preferredCountries={['FR', 'BE', 'CH', 'LU']}
                    fullWidth
                    size="small"
                    langOfCountryName={t('locale')}
                />
            </div>
            {/*<ToggleLine checked={isGnrSupplier} onChange={setIsGnrSupplier} title="GNR Supplier" />
            <ToggleLine checked={isSubcontractor} onChange={setIsSubcontractor} title="Subcontractor" />
            <ToggleLine checked={isToolSupplier} onChange={setIsToolSupplier} title="Tool Supplier" />
            <ToggleLine checked={isTransportCompany} onChange={setIsTransportCompany} title="Transport Company" />
            <ToggleLine checked={isSiteSupplier} onChange={setIsSiteSupplier} title="Site Supplies" />*/}
        </ModalBody>
    );
};

export default UpdateCompanyModal;
